import { useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import box from '~/assets/icons/box_3.svg';
import dead from '~/assets/icons/dead.svg';
import drivingSchool from '~/assets/icons/driving_school.svg';
import farm from '~/assets/icons/farm.svg';
import flash from '~/assets/icons/flash.svg';
import job from '~/assets/icons/job.svg';
import key from '~/assets/icons/key.svg';
import pizzaBox from '~/assets/icons/pizza_box.svg';
import race from '~/assets/icons/race.svg';
import war from '~/assets/icons/war.svg';
import { rage, useObservable } from '~/rage';

import { GameplayActivityTimerIcon } from '~/shared';
import { HeadInfoJobProgress } from '../HeadInfoJobProgress';
import { HeadInfoLevelUp } from '../HeadInfoLevelUp';
import { HeadInfoTimer } from '../HeadInfoTimer';
import { StyledContainer, StyledDot } from './HeadInfo.styled';

interface ITimerIcon {
	icon: string | undefined;
	color: string;
	invert?: boolean;
}

type TimerIcons = {
	[key in GameplayActivityTimerIcon]: ITimerIcon;
};

const timerIconDefs: TimerIcons = {
	[GameplayActivityTimerIcon.RentCar]: {
		icon: key,
		color: '#FFFFFF',
		invert: true,
	},
	[GameplayActivityTimerIcon.Dead]: {
		icon: dead,
		color: '#FFFFFF',
		invert: true,
	},
	[GameplayActivityTimerIcon.Job]: {
		icon: job,
		color: '#0085FF',
	},
	[GameplayActivityTimerIcon.Race]: {
		icon: race,
		color: '#FABE2C',
		invert: true,
	},
	[GameplayActivityTimerIcon.War]: {
		icon: war,
		color: '#E21633',
	},
	[GameplayActivityTimerIcon.Flash]: {
		icon: flash,
		color: '#FFFFFF',
		invert: true,
	},
	[GameplayActivityTimerIcon.PizzaBox]: {
		icon: pizzaBox,
		color: '#FFFFFF',
		invert: true,
	},
	[GameplayActivityTimerIcon.Box]: {
		icon: box,
		color: '#FFFFFF',
		invert: true,
	},
	[GameplayActivityTimerIcon.Farm]: {
		icon: farm,
		color: '#FFFFFF',
		invert: true,
	},
	[GameplayActivityTimerIcon.DrivingSchool]: {
		icon: drivingSchool,
		color: '#FFFFFF',
		invert: true,
	},
	[GameplayActivityTimerIcon.None]: {
		icon: undefined,
		color: '#FFFFFF',
	},
};

const HeadInfo: React.FC = () => {
	const activityTimer = useObservable(rage.game.getActivityTimer());
	const jobProgress = useObservable(rage.game.getJobProgress());

	const [activityTimerVisible, setActivityTimerVisible] =
		useState<boolean>(false);
	const [jobProgressVisible, setJobProgressVisible] = useState<boolean>(false);

	const [activityIconDef, activityDuration] = useMemo(
		() => [
			timerIconDefs[
				activityTimer ? activityTimer.icon : GameplayActivityTimerIcon.None
			],
			activityTimer ? Math.ceil(activityTimer.duration * 0.001) * 1000 : 0,
		],
		[activityTimer, timerIconDefs],
	);

	useEffect(() => {
		setActivityTimerVisible(activityDuration > 0);
	}, [activityDuration]);

	useEffect(() => {
		setJobProgressVisible(jobProgress !== null);
	}, [jobProgress]);

	return (
		<StyledContainer
			$timerVisible={activityTimerVisible}
			$progressVisible={jobProgressVisible}
		>
			<CSSTransition
				in={activityTimerVisible}
				classNames="timer"
				timeout={{
					appear: 500,
					enter: 500,
					exit: 750,
				}}
				unmountOnExit
			>
				<HeadInfoTimer
					icon={activityIconDef.icon}
					color={activityIconDef.color}
					invert={activityIconDef.invert}
					duration={activityDuration}
				/>
			</CSSTransition>
			<CSSTransition
				in={activityTimerVisible && jobProgressVisible}
				classNames="dot"
				timeout={{
					appear: 500,
					enter: 500,
					exit: 750,
				}}
				unmountOnExit
			>
				<StyledDot className={'dot'} />
			</CSSTransition>
			<CSSTransition
				in={jobProgressVisible}
				classNames="progress"
				timeout={{
					appear: 500,
					enter: 500,
					exit: 750,
				}}
				unmountOnExit
			>
				<HeadInfoJobProgress $progress={jobProgress} />
			</CSSTransition>
			{/* <CSSTransition in={messageVisible} classNames={'message'} timeout={500}>
				<HeadInfoLevelUp />
			</CSSTransition> */}
			<HeadInfoLevelUp />
		</StyledContainer>
	);
};

export { HeadInfo };
